/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import axios from 'axios';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import TextField from '../../components/textfield/TextField';
import {useIsPhone} from '../../hooks/useMedia';
import {TopNavSearchIcon} from '../../icons/TopNavSearchIcon';
import {
  dlsAlt,
  dlsColor,
  dlsLayout,
  dlsShadow,
  dlsTypography,
  dlsZIndex
} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {IconSize} from '../../types/types';
import {BodyTypography} from '../../typography/BodyTypography';
import {mapViewGoToOptions} from '../constants';
import {
  getLocationDetails,
  getLocationlabel,
  getLocations
} from '../helpers/MapViewUtils';
import {SearchPlaceItem} from '../search/SearchPlaceItem';
import {SEARCH_ZOOM_LEVEL} from '../search/SearchView';

interface StyleProps {
  isCurrentTheme?: boolean;
};

// defines the visual variants of the search field ...
export type SearchAddressVariant = 'default' | 'boxed';

// defines the prop definitions for the LayerChooser
export type SearchViewProps = {
  mapView: __esri.MapView;
  geofenceType: string;
  handleSetSelectedMapPoint?: Function;
  isVisible: boolean;
  isOpeningFromBottomSheet?: boolean;
  isSave?: boolean;
  useExtent?: boolean;
  closeSearchMap?: () => void;
  useHereApi?: boolean;
  // omits the built-in "select point on the map..." label, for callers that
  // render their own label above the search field ...
  hideLabel?: boolean;
  // shows the results as a dropdown over the content below the search field
  // instead of in line below it, so the caller's layout doesn't grow ...
  overlayResults?: boolean;
  // replaces the default "Enter Address, Coordinates or Places" placeholder ...
  placeholder?: string;
  // 'boxed' renders the current theme's flat bordered search box (leading icon
  // then a bare input) used by the vicinity search card; the default keeps the
  // shared TextField ...
  variant?: SearchAddressVariant;
};

export type SavedPlacesProps = {
  longitude: number;
  latitude: number;
  magicKey?: string;
  locationId?: string;
  text?: string;
  label?: string;
};

export type PlaceItemProps = {
  result: SavedPlacesProps;
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
  onDeleteItem?: Function;
  isSavedPlace?: boolean;
  placeSearchText?: string;
};

const MAX_SAVED_PLACES = 5;

// the tallest the overlaid results dropdown grows before it scrolls ...
const OVERLAY_RESULTS_MAX_HEIGHT = 240;

// function which returns the underline styles with custom border color...
const getUnderLineStyles = (borderColor = 'aaBlue') => ({
  borderBottomWidth : '1px solid !important',
  boxSizing         : 'border-box',
  borderColor       : `${dlsColor(borderColor)} !important`
});

// defines the custom styles for textfield...
export const textFieldStyles = {
  underline : {
    '&::before' : {
      ...getUnderLineStyles('grey3')
    },
    '&::after' : {
      ...getUnderLineStyles()
    },
    '&:hover' : {
      '&::before, &::after' : {
        ...getUnderLineStyles(),
        transition : 'none'
      }
    }
  },
  noPadLeft : {
    paddingLeft      : '0px !important',
    color            : 'inherit',
    fontFamily       : 'Roboto !important',
    fontStyle        : 'normal',
    fontWeight       : 400,
    fontSize         : '16px !important',
    lineHeight       : '20px !important',
    letterSpacing    : '0.5px !important',
    padding          : ({isCurrentTheme}: StyleProps) => isCurrentTheme ? '8px' : undefined,
    '&::placeholder' : {
      color         : () => `${dlsColor('grey1')}`,
      fontFamily    : 'Roboto !important',
      fontStyle     : 'normal',
      fontWeight    : 400,
      fontSize      : '13px !important',
      lineHeight    : '20px !important',
      letterSpacing : '0.5px !important'
    }
  }
};

// defines the styles used by the LayerChooser
export const useStyles = makeStyles((theme) => ({
  mapView : {
    backgroundColor : dlsColor('white'),
    top             : 0,
    maxWidth        : 414
  },
  searchIcon : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    marginTop      : ({isCurrentTheme}: StyleProps) => isCurrentTheme ? '6px' : undefined
  },
  searchMapFieldLabel : {
    color         : dlsColor('grey3'),
    marginBottom  : '8px',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontWeight    : 400,
    fontSize      : '14px !important',
    lineHeight    : '20px !important',
    letterSpacing : '0.5px !important',
    ...dlsTypography('body2')
  },
  mapViewMobile : {
    padding : '30px 8px 0px 8px'
  },
  searchListBox : {
    marginTop : dlsLayout('spacing')
  },
  searchBoxFullWidth : {
    width : '100%'
  },
  searchList : {
    marginTop : dlsLayout('margins')
  },
  searchBox : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'row',
    alignItems      : 'center',
    alignSelf       : 'stretch',
    gap             : '8px',
    padding         : '8px',
    width           : '100%',
    height          : '44px',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : '8px'
  },
  searchBoxIcon : {
    display    : 'flex',
    flex       : 'none',
    alignItems : 'center'
  },
  searchBoxInput : {
    flexGrow         : 1,
    minWidth         : 0,
    margin           : 0,
    padding          : 0,
    border           : 'none',
    outline          : 'none',
    background       : 'transparent',
    color            : dlsColor('grey1'),
    fontFamily       : 'Inter',
    fontStyle        : 'normal',
    fontWeight       : 400,
    fontSize         : '14px',
    lineHeight       : '16px',
    '&::placeholder' : {
      color   : dlsColor('grey3'),
      opacity : 1
    }
  },
  searchOverlayRoot : {
    position : 'relative'
  },
  searchListOverlay : {
    position        : 'absolute',
    top             : '100%',
    left            : 0,
    right           : 0,
    marginTop       : 4,
    zIndex          : dlsZIndex('dropdownPopper') as any,
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxShadow       : dlsShadow(2)
  },
  searchItem : {
    display    : 'flex',
    alignItems : 'center',
    minHeight  : '48px',
    cursor     : 'default',
    '&:hover'  : {
      backgroundColor : dlsAlt('colors', 'paleBlue2')
    }
  },
  searchItemElement : {
    paddingLeft  : dlsLayout('margins') / 2,
    paddingRight : dlsLayout('margins') / 3
  },
  ...textFieldStyles
}));

function SearchAddress({
  mapView,
  geofenceType,
  isVisible,
  closeSearchMap,
  useExtent,
  isSave,
  handleSetSelectedMapPoint,
  useHereApi = false,
  isOpeningFromBottomSheet = false,
  placeholder,
  hideLabel = false,
  overlayResults = false,
  variant = 'default'
}: SearchViewProps) {
  const {isCurrent: isCurrentTheme}                 = useThemeContext();
  const classes                                     = useStyles({isCurrentTheme});
  const isPhone                                     = useIsPhone();
  const [results, setResults]                       = useState(null);
  const [isShowSaveButton, setIsShowSaveButton]     = useState(false);
  const [savedPlaces, setSavedPlaces]               = useState<SavedPlacesProps[]>(null);
  const [isMobileFullHeight, setIsMobileFullHeight] = useState(false);
  const [placeSearchText, setPlaceSearchText]       = useState('');
  const currentSelectedPlace                        = useRef<SavedPlacesProps | null>(null);
  const sourceCancelToken                           = useRef(null);
  const savedPlacesRaf                              = useRef(null);

  const isBoxed           = variant === 'boxed';
  const searchPlaceholder = placeholder ?? `${translate('maps:geofences.placeHolder')}`;

  async function searchLocation(val) {
    try {
      const searchText = val?.trim() || '';
      const matchedSavePlaces = savedPlacesRaf?.current?.filter(
        (place) =>
          !place?.text?.toLowerCase().indexOf(searchText?.toLowerCase())
      );
      setSavedPlaces(matchedSavePlaces);
      if (searchText) {
        const cancelToken = axios.CancelToken;
        if (typeof sourceCancelToken.current !== typeof undefined) {
          sourceCancelToken.current?.cancel();
        }
        sourceCancelToken.current = cancelToken.source();
        const suggestions = await getLocations({
          cancelToken : sourceCancelToken.current.token,
          searchText,
          useHereApi,
          mapView,
          useExtent
        });
        setResults(suggestions);
      } else {
        setResults(null);
        if (typeof sourceCancelToken.current !== typeof undefined) {
          sourceCancelToken.current?.cancel();
        }
      }
    } catch (err) {
      if (!axios.isCancel(err.error)) {
        error(err);
      }
    }
  }

  const searchOnChange = debounce(function(value) {
    setIsMobileFullHeight(true);
    setIsShowSaveButton(false);
    searchLocation(value);
    if (!value) {
      setResults(null);
    }
  }, 200);

  const onSearchChange = function({target: {value}}) {
    setPlaceSearchText(value);
    searchOnChange(value);
  };

  async function callAddress(selectedResult) {
    try {
      const selectedLocation = await getLocationDetails(selectedResult) as unknown as SavedPlacesProps;
      const {longitude, latitude}  = selectedLocation;
      mapView.zoom                 = SEARCH_ZOOM_LEVEL;
      currentSelectedPlace.current = selectedLocation;
      mapView.goTo({center: [longitude, latitude]}, mapViewGoToOptions);
      handleSetSelectedMapPoint?.({
        current : {
          longitude,
          latitude
        }
      });
    } catch (err) {
      error(err);
    }
  }

  return (
    <div className={classnames(
      isOpeningFromBottomSheet ? classes.mapViewMobile : classes.mapView,
      overlayResults && classes.searchOverlayRoot
    )} style={{
      display : !isVisible ? 'none' : 'inherit',
      height :
      isMobileFullHeight && isOpeningFromBottomSheet ? 'calc(100vh - 78px)' : 'auto',
      paddingBottom : 0
    }}>
      {!hideLabel && geofenceType !== translate('maps:geofences.draw') && (
        <BodyTypography>{translate('maps:geofences.searchLabel')}</BodyTypography>
      )}
      <div className={hideLabel ? classes.searchBoxFullWidth : classes.searchListBox}>
        {isBoxed ? (
          <div className={classes.searchBox}>
            <div className={classes.searchBoxIcon}>
              <TopNavSearchIcon size={IconSize.medium} color={dlsColor('grey1')} />
            </div>
            <input className={classes.searchBoxInput}
              type='text'
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={placeSearchText}
              onChange={onSearchChange} />
          </div>
        ) : (
          <TextField placeholder={searchPlaceholder}
            style={{
              root : {
                width : '100%'
              }
            }}
            InputLabelProps = {{
              classes : {
                root : classes.searchMapFieldLabel
              }
            }}
            value={placeSearchText}
            InputProps={{
              classes : {
                underline : classes.underline,
                input     : classes.noPadLeft
              },
              startAdornment : (
                <div className={classes.searchIcon}>
                  <TopNavSearchIcon />
                </div>
              )
            }}
            onChange={onSearchChange} />
        )}
      </div>
      {(!!results?.length || !!savedPlaces?.length) && (
        <div className={classnames(classes.searchList, overlayResults && classes.searchListOverlay)}
          style={{
            overflow  : 'auto',
            maxHeight : overlayResults
              ? OVERLAY_RESULTS_MAX_HEIGHT
              : ((isOpeningFromBottomSheet || isPhone) ? 'calc(100vh - 200px)' : mapView.height - 200)
          }}>

          {results?.map((result) => {
            const {magicKey, locationId, text} = result;
            const searchText = getLocationlabel(text, useHereApi);
            return (
              <SearchPlaceItem key={magicKey || locationId}
                placeSearchText={placeSearchText}
                result={result}
                onClick={function() {
                  if (savedPlacesRaf.current?.length < MAX_SAVED_PLACES) {
                    setIsShowSaveButton(true);
                  }
                  const selectedResult = result;
                  callAddress(selectedResult);
                  setPlaceSearchText(searchText);
                  setResults(null);
                  setSavedPlaces(null);
                  setIsMobileFullHeight(false);
                }} />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SearchAddress;
